import { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PanelContext from "../context/PanelContext";
import DashboardContext from "../context/DashboardContext";

import Box from "@mui/material/Box";
import CategoriesRankingGraphicCard from "../components/panel/CategoriesRankingGraphicCard";
import Container from "@mui/material/Container";
import DiscountRanking from "../components/panel/DiscountRankingRow";
import MostUsedRow from "../components/panel/MostUsedRow";
import NumberOfUsesCard from "../components/panel/NumberOfUsesCard";
import Stack from "@mui/material/Stack";
import TitleAndFilter from "../components/panel/TitleAndFilter";
import Typography from "@mui/material/Typography";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { fetchResume, fetchAllEstablishments, activeInativeMembers } from "../services";
import { cpfFormatter, cpfSpecialCharactersRemover } from "../utils";
import { getPanelResume } from "../services/newAPI";
import { Skeleton } from "@mui/material";

const meses = {
  '01': "Janeiro",
  '02': "Fevereiro",
  '03': "Março",
  '04': "Abril",
  '05': "Maio",
  '06': "Junho",
  '07': "Julho",
  '08': "Agosto",
  '09': "Setembro",
  '10': "Outubro",
  '11': "Novembro",
  '12': "Dezembro",
}

const formatarMes = (month) => month < 10 ? `0${month}` : month

export default function Panel() {
  const today = new Date();
  const pegaMes = formatarMes(Number(today.getMonth() === 0 ? 12 : today.getMonth()));
  const pegaAno = today.getFullYear();
  const [filterTextInput, setFilterTextInput] = useState("");
  const [cpfError, setCpfError] = useState(false);
  const [filterState, setFilterState] = useState(false);
  const [month, setMonth] = useState(pegaMes);
  const [year, setYear] = useState(pegaAno);

  const {
    panelTitle,
    setPanelTitle,
    clientId,
    clientUser,
    clientPassword,
    nodeToken,
  } = useContext(DashboardContext);

  const { data: resume } = useQuery(["resume"], fetchResume);

  const { data: favoriteEstablishments } = useQuery(
    "favorites",
    () =>
      fetchAllEstablishments(
        clientUser,
        clientPassword,
        "1",
        false,
        "1",
        "",
        "",
        ""
      )
  );

  const { data: newReport, isLoading } = useQuery(
    ["report", filterTextInput, month, year],
    () =>
      getPanelResume({
        token: nodeToken,
        cpf: filterTextInput ? cpfSpecialCharactersRemover(filterTextInput) : null,
        date: month && year ? `${year}-${month}` : null,
      }),
    {
      enabled: !!clientId,
    }
  );


  const handleFilterInputChange = ({ target: { value } }) => {
    const onlyIntegerRegex = "^[0-9]*$";
    if (value.match(onlyIntegerRegex)) setFilterTextInput(value);
  };

  const handleResetFilterButton = () => {
    setCpfError(false);
    setFilterTextInput("");
    setPanelTitle(JSON.parse(sessionStorage.getItem("panelTitle")));
  };

  const submitFilter = async (event) => {
    event.preventDefault();
    if (filterTextInput.length >= 11 || month) {
      setFilterState(true);
      setCpfError(false);
      setPanelTitle(
        cpfFormatter(
          filterTextInput.length >= 11 ? filterTextInput : meses[month]
        )
      );
    } else {
      setFilterState(false);
    }
  };

  const context = {
    filterTextInput,
    handleFilterInputChange,
    submitFilter,
    panelTitle,
    handleResetFilterButton,
    cpfError,
  };

  return (
    <PanelContext.Provider value={context} id="Panel">
      <Container sx={{ minWidth: 1100 }}>
        <Stack spacing={3} mb={3} className="gap-6">
          <TitleAndFilter
            setFilterState={setFilterState}
            month={month}
            year={year}
            setMonth={setMonth}
            setYear={setYear}
          />

          <Stack direction="row" spacing={3} className="gap-6">
            {isLoading ? (
              <Skeleton
                animation="wave"
                sx={{ borderRadius: "40px" }}
                variant="rectangular"
                width="100%"
              >
                <div style={{ paddingTop: "40%" }} />
              </Skeleton>
            ) : (
              <NumberOfUsesCard
                totalOfUses={newReport?.oldUtilizations}
                totalOfInteractions={newReport?.userUtilizationsCount}
              />
            )}
            {isLoading ? (
              <Skeleton
                animation="wave"
                sx={{ borderRadius: "40px" }}
                variant="rectangular"
                width="100%"
              >
                <div style={{ paddingTop: "17%" }} />
              </Skeleton>
            ) : !newReport?.countCategories?.length ? (
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                  width: "100%",
                }}
              >
                <Typography component="p" textAlign="center" variant="h4">
                  Nenhum dado encontrado
                </Typography>
              </Box>
            ) : (
              <CategoriesRankingGraphicCard
                wasUserFound={!!newReport?.countCategories}
                categorias={newReport?.countCategories}
              />
            )}
          </Stack>

          {isLoading ? (
            <Skeleton
              animation="wave"
              sx={{ borderRadius: "40px" }}
              variant="rectangular"
              width="100%"
            >
              <div style={{ paddingTop: "17%" }} />
            </Skeleton>
          ) : !newReport?.establishments ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
                width: "100%",
              }}
            >
              <Typography component="p" my={3} textAlign="center" variant="h4">
                Nenhum dado encontrado
              </Typography>
            </Box>
          ) : (
            <MostUsedRow mostUsedEstablishments={newReport?.establishments} />
          )}

          {!isLoading && !!newReport?.users ? (
            <DiscountRanking discountRanking={newReport?.users} />
          ) : !filterState ? (
            <Skeleton
              animation="wave"
              sx={{ borderRadius: "40px" }}
              variant="rectangular"
              width="100%"
            >
              <div style={{ paddingTop: "17%" }} />
            </Skeleton>
          ) : (
            <></>
          )}
        </Stack>
      </Container>
    </PanelContext.Provider>
  );
}
